import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'

type ThemePreferenceContextValue = {
  themePreference: ThemePreference
  setThemePreference: (theme: ThemePreference) => void
}

const STORAGE_KEY = 'calendly_publisher_theme_preference'

const defaultContextValue: ThemePreferenceContextValue = {
  themePreference: 'light',
  setThemePreference: () => {},
}

const ThemePreferenceContext = createContext<ThemePreferenceContextValue>(defaultContextValue)

function readFromStorage(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // localStorage may be unavailable (e.g. private browsing with strict settings)
  }
  return 'light'
}

/**
 * Provides persisted theme preference state to the app.
 * Mount once near the app root. Persists to localStorage and syncs across tabs.
 */
export function ThemePreferenceProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(readFromStorage)

  const setThemePreference = useCallback((theme: ThemePreference) => {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // ignore write failures
    }
    setThemePreferenceState(theme)
  }, [])

  // Sync changes made in other tabs
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return
      const value = event.newValue
      if (value === 'light' || value === 'dark' || value === 'system') setThemePreferenceState(value)
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const value = useMemo(
    () => ({ themePreference, setThemePreference }),
    [themePreference, setThemePreference]
  )

  return (
    <ThemePreferenceContext.Provider value={value}>
      {children}
    </ThemePreferenceContext.Provider>
  )
}

export function useThemePreference(): ThemePreferenceContextValue {
  return useContext(ThemePreferenceContext)
}
